import React, { useMemo, useState } from "react";
import {
  Search,
  Plus,
  MapPin,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../styles/Locations.css";

const locationData = [
  {
    id: 1,
    name: "Erode",
    state: "Tamil Nadu",
    type: "City",
    pincode: "638001",
    status: "Active",
  },
  {
    id: 2,
    name: "Coimbatore",
    state: "Tamil Nadu",
    type: "City",
    pincode: "641001",
    status: "Active",
  },
  {
    id: 3,
    name: "Chennai",
    state: "Tamil Nadu",
    type: "City",
    pincode: "600001",
    status: "Active",
  },
  {
    id: 4,
    name: "Salem",
    state: "Tamil Nadu",
    type: "City",
    pincode: "636001",
    status: "Active",
  },
  {
    id: 5,
    name: "Bangalore",
    state: "Karnataka",
    type: "City",
    pincode: "560001",
    status: "Active",
  },
  {
    id: 6,
    name: "Madurai",
    state: "Tamil Nadu",
    type: "City",
    pincode: "625001",
    status: "Inactive",
  },
  {
    id: 7,
    name: "Trichy",
    state: "Tamil Nadu",
    type: "City",
    pincode: "620001",
    status: "Active",
  },
  {
    id: 8,
    name: "Tiruppur",
    state: "Tamil Nadu",
    type: "City",
    pincode: "641601",
    status: "Active",
  },
];

export default function Locations() {
  const [locations, setLocations] = useState(locationData);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 6;

  const filteredLocations = useMemo(() => {
    return locations.filter((location) => {
      const searchText = search.toLowerCase();

      const matchesSearch =
        location.name.toLowerCase().includes(searchText) ||
        location.state.toLowerCase().includes(searchText) ||
        location.pincode.includes(searchText);

      const matchesState =
        !stateFilter || location.state === stateFilter;

      const matchesType =
        !typeFilter || location.type === typeFilter;

      const matchesStatus =
        !statusFilter || location.status === statusFilter;

      return (
        matchesSearch &&
        matchesState &&
        matchesType &&
        matchesStatus
      );
    });
  }, [locations, search, stateFilter, typeFilter, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredLocations.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedLocations = filteredLocations.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = locations.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = locations.filter(
    (item) => item.status === "Inactive"
  ).length;

  const clearFilters = () => {
    setSearch("");
    setStateFilter("");
    setTypeFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const handleStatusToggle = (id) => {
    setLocations((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const handleDelete = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this location?"
    );

    if (!confirmed) return;

    setLocations((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  return (
    <div className="locations-page">

      {/* Page Header */}
      <div className="locations-header">
        <div>
          <div className="locations-title-row">
            <div className="locations-title-icon">
              <MapPin size={22} />
            </div>

            <div>
              <h1>Locations</h1>
              <p>
                Manage serviceable cities and locations
              </p>
            </div>
          </div>
        </div>

        <button className="primary-btn">
          <Plus size={18} />
          Add Location
        </button>
      </div>

      {/* Summary Cards */}
      <div className="location-summary">

        <div className="location-summary-card">
          <div className="summary-icon total">
            <MapPin size={20} />
          </div>

          <div>
            <span>Total Locations</span>
            <strong>{locations.length}</strong>
          </div>
        </div>

        <div className="location-summary-card">
          <div className="summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Locations</span>
            <strong>{activeCount}</strong>
          </div>
        </div>

        <div className="location-summary-card">
          <div className="summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Locations</span>
            <strong>{inactiveCount}</strong>
          </div>
        </div>

      </div>

      {/* Filters */}
      <div className="locations-filter-card">

        <div className="location-search">
          <Search size={18} />

          <input
            type="text"
            placeholder="Search city, state or pincode..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          value={stateFilter}
          onChange={(e) => {
            setStateFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All States</option>
          <option value="Tamil Nadu">Tamil Nadu</option>
          <option value="Karnataka">Karnataka</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Types</option>
          <option value="City">City</option>
          <option value="District">District</option>
          <option value="Area">Area</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search ||
          stateFilter ||
          typeFilter ||
          statusFilter) && (
          <button
            className="clear-filter-btn"
            onClick={clearFilters}
          >
            <X size={16} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="locations-table-card">

        <div className="table-responsive">

          <table className="locations-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Location</th>
                <th>State</th>
                <th>Type</th>
                <th>Pincode</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {paginatedLocations.length > 0 ? (
                paginatedLocations.map((location, index) => (
                  <tr key={location.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="location-name">
                        <div className="location-avatar">
                          <MapPin size={17} />
                        </div>

                        <strong>{location.name}</strong>
                      </div>
                    </td>

                    <td>{location.state}</td>

                    <td>
                      <span className="type-badge">
                        {location.type}
                      </span>
                    </td>

                    <td>{location.pincode}</td>

                    <td>
                      <button
                        className={`status-badge ${
                          location.status === "Active"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                        onClick={() =>
                          handleStatusToggle(location.id)
                        }
                      >
                        {location.status}
                      </button>
                    </td>

                    <td>
                      <div className="location-actions">

                        <button
                          className="icon-action edit"
                          title="Edit"
                        >
                          <Pencil size={17} />
                        </button>

                        <button
                          className="icon-action delete"
                          title="Delete"
                          onClick={() =>
                            handleDelete(location.id)
                          }
                        >
                          <Trash2 size={17} />
                        </button>

                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="empty-location"
                  >
                    No locations found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="location-pagination">

          <span>
            Showing{" "}
            {filteredLocations.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredLocations.length
            )}{" "}
            of {filteredLocations.length}
          </span>

          <div className="pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) => Math.max(1, prev - 1))
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(totalPages, prev + 1)
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile Cards */}
      <div className="locations-mobile-list">

        {paginatedLocations.length > 0 ? (
          paginatedLocations.map((location) => (
            <div
              className="location-mobile-card"
              key={location.id}
            >

              <div className="mobile-location-top">

                <div className="location-name">
                  <div className="location-avatar">
                    <MapPin size={17} />
                  </div>

                  <div>
                    <strong>{location.name}</strong>
                    <span>{location.state}</span>
                  </div>
                </div>

                <button
                  className={`status-badge ${
                    location.status === "Active"
                      ? "status-active"
                      : "status-inactive"
                  }`}
                  onClick={() =>
                    handleStatusToggle(location.id)
                  }
                >
                  {location.status}
                </button>

              </div>

              <div className="mobile-location-details">

                <div>
                  <span>Type</span>
                  <strong>{location.type}</strong>
                </div>

                <div>
                  <span>Pincode</span>
                  <strong>{location.pincode}</strong>
                </div>

              </div>

              <div className="mobile-location-actions">

                <button className="mobile-edit-btn">
                  <Pencil size={16} />
                  Edit
                </button>

                <button
                  className="mobile-delete-btn"
                  onClick={() =>
                    handleDelete(location.id)
                  }
                >
                  <Trash2 size={16} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="mobile-empty-location">
            No locations found
          </div>
        )}

      </div>

    </div>
  );
}